import { inject } from '@angular/core';
import { WINDOW } from '@ng-web-apis/common';
import { createEffect, Actions, ofType } from '@ngrx/effects';
import { filter, map } from 'rxjs';

import { Ga4ContentType, Ga4EventLocation, GA4ImActionType } from '../models/event-data.model';
import { GA4EventType } from '../models/general.model';
import { GA4AnalyticsPublicActions } from '../public.api';

import { AnalyticsClickSourceID, EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';
import { ObservedElementClick } from '@shared/analytics/store/app-events-analytics.actions';

/**
 * LÓGICA CENTRALIZADA PARA LOS EVENTOS SELECT_CONTENT QUE NO
 * REQUIEREN DE LÓGICA ESPECÍFICA PARA GENERAR SUS PROPIEDADES
 */

interface SelectContentEventsConfig {
    contentType: Ga4ContentType;
    eventFrom: EventFrom;
    imActionType: GA4ImActionType;
    imLocation: Ga4EventLocation;
}

const SELECT_CONTENT_EVENTS_CONFIG: Partial<Record<AnalyticsClickSourceID, SelectContentEventsConfig>> = {
    [AnalyticsClickSourceID.feelAppointmentsTunnelCheckAppointments]: {
        contentType: Ga4ContentType.loyalty,
        eventFrom: EventFrom.feelJoin,
        imActionType: GA4ImActionType.ver_citas,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.headerViewMinicart]: {
        contentType: Ga4ContentType.cart,
        eventFrom: undefined,
        imActionType: GA4ImActionType.ver_minicesta,
        imLocation: Ga4EventLocation.header,
    },
    [AnalyticsClickSourceID.minicartGoToShopcart]: {
        contentType: Ga4ContentType.cart,
        eventFrom: undefined,
        imActionType: GA4ImActionType.ir_a_cesta,
        imLocation: Ga4EventLocation.sidebar,
    },
    [AnalyticsClickSourceID.myAccountfeelHeaderJoinFeel]: {
        contentType: Ga4ContentType.loyalty,
        eventFrom: EventFrom['app-feel-subscription-modal'],
        imActionType: GA4ImActionType.unir_programa,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.orderReturnRecommendationGoStoreLocator]: {
        contentType: Ga4ContentType.refund,
        eventFrom: EventFrom.return,
        imActionType: GA4ImActionType.ir_a_localizador_tiendas,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.orderReturnRecommendationNextStep]: {
        contentType: Ga4ContentType.refund,
        eventFrom: EventFrom.return,
        imActionType: GA4ImActionType.siguiente_paso,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPChangesAndReturns]: {
        contentType: Ga4ContentType.product_info,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_cambios_devoluciones,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPCustomizeProduct]: {
        contentType: Ga4ContentType.product_custom,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.personalizar,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPOpenCoordinatedProducts]: {
        contentType: Ga4ContentType.product_related,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_producto_coordinado,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPOpenSizeGuide]: {
        contentType: Ga4ContentType.product_info,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_guia_tallas,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPSeeProductComposition]: {
        contentType: Ga4ContentType.product_info,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_composicion,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPOutOfStockSeeSimilar]: {
        contentType: Ga4ContentType.product_info,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_similares_sin_stock,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPShowLookForProduct]: {
        contentType: Ga4ContentType.product_related,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_look,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.PDPViewStoreAvailability]: {
        contentType: Ga4ContentType.product_info,
        eventFrom: EventFrom.productdetail,
        imActionType: GA4ImActionType.ver_disponibilidad_en_tienda,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.shopcartPageShopcartTab]: {
        contentType: Ga4ContentType.cart,
        eventFrom: undefined,
        imActionType: GA4ImActionType.ver_cesta,
        imLocation: EventFrom.shopcart as unknown as Ga4EventLocation,
    },
    [AnalyticsClickSourceID.shopTheLookAddAllToCart]: {
        contentType: Ga4ContentType.ficha_bundle,
        eventFrom: EventFrom.fichaBundle,
        imActionType: GA4ImActionType.anadir_todo_cesta,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.sidebarViewMinicart]: {
        contentType: Ga4ContentType.cart,
        eventFrom: undefined,
        imActionType: GA4ImActionType.ver_minicesta,
        imLocation: Ga4EventLocation.header,
    },
    [AnalyticsClickSourceID.smartAssistantOpenChat]: {
        contentType: Ga4ContentType.smart_assistant,
        eventFrom: EventFrom.smartAssistant,
        imActionType: GA4ImActionType.chat_interaction,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.smartAssistantViewOrder]: {
        contentType: Ga4ContentType.smart_assistant,
        eventFrom: EventFrom.smartAssistant,
        imActionType: GA4ImActionType.order_status_interaction,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.smartAssistantViewReturn]: {
        contentType: Ga4ContentType.smart_assistant,
        eventFrom: EventFrom.smartAssistant,
        imActionType: GA4ImActionType.return_status_interaction,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.smartAssistantViewOrderFaqs]: {
        contentType: Ga4ContentType.smart_assistant,
        eventFrom: EventFrom.smartAssistant,
        imActionType: GA4ImActionType.order_questions,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.smartAssistantViewReturnFaqs]: {
        contentType: Ga4ContentType.smart_assistant,
        eventFrom: EventFrom.smartAssistant,
        imActionType: GA4ImActionType.return_questions,
        imLocation: undefined,
    },
    [AnalyticsClickSourceID.miniCartGoWishlist]: {
        contentType: Ga4ContentType.wishlist,
        eventFrom: undefined,
        imActionType: GA4ImActionType.ver_wishlist,
        imLocation: Ga4EventLocation.wishlist,
    },
};

const getContentIdByClickId = (clickId: AnalyticsClickSourceID, observedData: any): string => {
    switch (clickId) {
        case AnalyticsClickSourceID.PDPChangesAndReturns:
        case AnalyticsClickSourceID.PDPCustomizeProduct:
        case AnalyticsClickSourceID.PDPOpenCoordinatedProducts:
        case AnalyticsClickSourceID.PDPOpenSizeGuide:
        case AnalyticsClickSourceID.PDPSeeProductComposition:
        case AnalyticsClickSourceID.PDPOutOfStockSeeSimilar:
        case AnalyticsClickSourceID.PDPShowLookForProduct:
        case AnalyticsClickSourceID.PDPViewStoreAvailability:
        case AnalyticsClickSourceID.shopTheLookAddAllToCart:
            return observedData?.product?.reference;
        default:
            return undefined;
    }
};

const getItemsByClickId = (clickId: AnalyticsClickSourceID, observedData: any): any[] => {
    switch (clickId) {
        case AnalyticsClickSourceID.PDPChangesAndReturns:
        case AnalyticsClickSourceID.PDPCustomizeProduct:
        case AnalyticsClickSourceID.PDPOpenCoordinatedProducts:
        case AnalyticsClickSourceID.PDPOpenSizeGuide:
        case AnalyticsClickSourceID.PDPSeeProductComposition:
        case AnalyticsClickSourceID.PDPShowLookForProduct:
        case AnalyticsClickSourceID.PDPViewStoreAvailability:
        case AnalyticsClickSourceID.shopTheLookAddAllToCart:
            return [observedData?.product];
        default:
            return undefined;
    }
};

export const SelectContentEvents$ = createEffect((
    actions$ = inject(Actions),
    commonWindow = inject(WINDOW),
) => actions$.pipe(
    ofType(ObservedElementClick),
    filter(({ observeType, observedData }) => observeType === 'analyticsClick' && Object.keys(SELECT_CONTENT_EVENTS_CONFIG).includes(observedData?.clickId)),
    map(({ elementReference, observedData }) => {
        const eventConfig = SELECT_CONTENT_EVENTS_CONFIG[observedData?.clickId];
        const imLinkText = commonWindow.document.querySelector(`[data-reference="${elementReference}"]`)?.textContent?.trim();
        const contentId = getContentIdByClickId(observedData?.clickId, observedData);
        const items = getItemsByClickId(observedData?.clickId, observedData);

        return GA4AnalyticsPublicActions.Ga4Event({
            name: GA4EventType.select_content,
            eventFrom: eventConfig.eventFrom,
            data: {
                contentId,
                contentType: eventConfig.contentType,
                imActionType: eventConfig.imActionType,
                imLinkText,
                imLocation: eventConfig.imLocation,
                items,
            },
        });
    })
), { functional: true });

export const GA4AnalyticsSelectContentEffects = {
    SelectContentEvents$,
};
